import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Comparison data                                                     */
/* ------------------------------------------------------------------ */

const COMPARISON = [
  {
    feature: "Typical fee",
    highStreet: "1.0-1.5% + VAT (on completion)",
    online: "\u00a3999-1,499 flat fee (often upfront)",
  },
  {
    feature: "When you pay",
    highStreet: "On completion only",
    online: "Upfront or deferred (with surcharge)",
  },
  {
    feature: "Accompanied viewings",
    highStreet: "Yes, usually included",
    online: "Rarely; you conduct your own viewings",
  },
  {
    feature: "Local market knowledge",
    highStreet: "Strong; office in your area",
    online: "Limited; call-centre based support",
  },
  {
    feature: "Photography",
    highStreet: "Professional photographer visits",
    online: "Professional or DIY depending on package",
  },
  {
    feature: "Video marketing",
    highStreet: "Offered by forward-thinking agents",
    online: "Rarely included",
  },
  {
    feature: "For-sale board",
    highStreet: "Included as standard",
    online: "Usually an add-on",
  },
  {
    feature: "Negotiation",
    highStreet: "Agent negotiates on your behalf",
    online: "You may negotiate directly with buyers",
  },
  {
    feature: "Sale progression",
    highStreet: "Dedicated progressor chases solicitors",
    online: "Basic; often limited to email updates",
  },
  {
    feature: "Portal listings",
    highStreet: "Rightmove, Zoopla, OnTheMarket",
    online: "Rightmove, Zoopla (varies by provider)",
  },
]

const FAQ = [
  {
    q: "Are online estate agents cheaper than high street agents?",
    a: "On paper, yes. A flat fee of \u00a3999-1,499 is less than 1-1.5% of most property values. However, online agents often charge upfront regardless of whether the property sells, and studies suggest properties sold through local agents achieve higher sale prices on average. When you factor in the higher achieved price, the high street agent may deliver better net proceeds.",
  },
  {
    q: "Can I use both an online and a high street agent?",
    a: "You can, but be careful. If you instruct a high street agent on a sole agency basis, using an online agent simultaneously would breach that contract. If you want to use both, you would need a multi-agency agreement, which typically carries higher fees. Speak to both agents about your options before signing anything.",
  },
  {
    q: "Do online agents list on Rightmove?",
    a: "Most established online agents list on Rightmove and Zoopla. However, the listing quality varies significantly. Without professional photography, video, and well-written descriptions, a portal listing alone is unlikely to generate strong interest. The listing is only as good as the marketing behind it.",
  },
  {
    q: "What if my house does not sell with an online agent?",
    a: "If you paid upfront, you have already spent that money regardless of outcome. Some online agents offer a deferred payment option, but this usually costs more. If the property does not sell, you may end up paying a second fee to instruct a high street agent. With a traditional agent, you typically only pay on completion.",
  },
  {
    q: "Which type of agent is better for expensive properties?",
    a: "For higher-value properties, a high street agent with premium marketing (video, social media, targeted advertising) generally delivers better results. Buyers at the upper end of the market expect a high level of service, and the percentage-based fee becomes more significant, but so does the difference in achieved sale price.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function OnlineVsHighStreetGuide({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Online vs High Street Estate Agents: Which Is Right for You?",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Online vs High Street Agents" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Online vs high street estate agents
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Flat-fee online agents or commission-based high street agents?
            A balanced, data-backed comparison to help you decide which
            type of estate agent is right for your property.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The estate agent landscape in 2026
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The rise of online estate agents like Purplebricks, Strike,
              and Yopa promised to disrupt the property industry with lower
              fees and a simpler process. A decade on, the picture is more
              nuanced. Online agents hold a small share of the market,
              while high street agents continue to handle the vast majority
              of UK property sales.
            </p>
            <p>
              Both models have genuine strengths and weaknesses. The right
              choice depends on your property, your priorities, and how
              much hands-on support you need during the sale. This guide
              compares the two approaches across every dimension that
              matters.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Side-by-side comparison
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            How online and high street agents compare on the things that
            matter most.
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Feature
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    High street
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Online
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {COMPARISON.map((row) => (
                  <tr key={row.feature} className="hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {row.feature}
                    </td>
                    <td className="px-4 py-3 text-slate">
                      {row.highStreet}
                    </td>
                    <td className="px-4 py-3 text-slate">{row.online}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* High street strengths */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The case for high street agents
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              High street agents earn their fee through local expertise,
              personal service, and a genuine incentive to achieve the
              highest possible price. Because they are paid a percentage on
              completion, their interests are directly aligned with yours.
            </p>
            <p>
              Accompanied viewings are a significant advantage. A skilled
              agent can read buyer body language, answer questions on the
              spot, and sell the lifestyle of the property in a way that no
              virtual tour or listing description can replicate. They also
              gather real-time feedback, which helps refine pricing and
              marketing strategy.
            </p>
            <p>
              The best high street agents also invest in modern marketing.
              Professional photography, floorplans, and property video are
              increasingly standard at forward-thinking agencies. Agents
              who use video generate twice as many enquiries and attract
              more qualified buyers.
            </p>
          </div>

          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            The case for online agents
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Online agents offer a clear cost advantage, particularly for
              higher-value properties. A flat fee of \u00a3999 on a
              \u00a3500,000 property represents just 0.2% of the sale
              price, compared to \u00a35,000-7,500 + VAT for a
              traditional agent.
            </p>
            <p>
              The process is often more technology-driven, with online
              dashboards, instant notifications, and self-service tools.
              For confident sellers who are comfortable conducting their
              own viewings and negotiating directly with buyers, online
              agents can deliver genuine savings.
            </p>
            <p>
              However, the trade-off is clear: less hands-on support, less
              local knowledge, and often a less comprehensive marketing
              package. The upfront fee model also means you carry the
              financial risk if the property does not sell.
            </p>
          </div>
        </div>
      </section>

      {/* The real cost comparison */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The real cost comparison
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Fee comparisons are misleading without considering achieved
              sale prices. Industry data consistently shows that properties
              sold through high street agents achieve higher prices on
              average. Here is a worked example:
            </p>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-lg font-bold text-charcoal">
                Online agent
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm text-slate">
                <li>
                  Asking price: <strong className="text-charcoal">\u00a3350,000</strong>
                </li>
                <li>
                  Achieved price: <strong className="text-charcoal">\u00a3335,000</strong>{" "}
                  (95.7%)
                </li>
                <li>
                  Agent fee: <strong className="text-charcoal">\u00a3999</strong>{" "}
                  (fixed)
                </li>
                <li>
                  Net proceeds:{" "}
                  <strong className="text-charcoal">\u00a3334,001</strong>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-red/20 bg-white p-5">
              <h3 className="font-display text-lg font-bold text-charcoal">
                High street agent
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm text-slate">
                <li>
                  Asking price: <strong className="text-charcoal">\u00a3350,000</strong>
                </li>
                <li>
                  Achieved price: <strong className="text-charcoal">\u00a3345,000</strong>{" "}
                  (98.6%)
                </li>
                <li>
                  Agent fee: <strong className="text-charcoal">\u00a34,140</strong>{" "}
                  (1.2% + VAT)
                </li>
                <li>
                  Net proceeds:{" "}
                  <strong className="text-red">\u00a3340,860</strong>
                </li>
              </ul>
            </div>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-slate">
            In this example, the high street agent costs \u00a33,141 more
            in fees but achieves \u00a310,000 more on the sale price.
            The seller nets <strong className="text-charcoal">\u00a36,859 more</strong>{" "}
            with the high street agent despite the higher fee. This
            pattern holds across most price brackets in the UK market.
          </p>
        </div>
      </section>

      {/* Marketing difference */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The marketing gap
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Marketing quality is where the two models diverge most
              sharply. High street agents increasingly offer professional
              photography, floorplans, video walkthroughs, and social
              media campaigns as standard. Online agents typically offer
              basic photography and portal listings.
            </p>
            <p>
              This matters because 95% of buyers start their search
              online. The quality of the listing determines whether they
              click, whether they book a viewing, and how much they are
              willing to offer. Properties with video receive 2x more
              enquiries, and 73% of vendors say they prefer agents who
              offer video marketing.
            </p>
            <p>
              Tools like{" "}
              <Link
                href="/features"
                className="text-red underline decoration-red/30 transition-colors hover:text-deep-red"
              >
                QuiteRight
              </Link>{" "}
              are helping high street agents close this gap further by
              making it easy to produce professional video content for
              every listing, not just the premium ones.
            </p>
          </div>
        </div>
      </section>

      {/* When to choose which */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Which is right for you
            <span className="text-red">?</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-base font-bold text-charcoal">
                Choose a high street agent if:
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate">
                {[
                  "You want accompanied viewings and hands-on support",
                  "Local market knowledge matters for your area",
                  "You want to maximise your sale price",
                  "You prefer to pay only when the sale completes",
                  "Your property is unusual, high-value, or needs careful marketing",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="mt-0.5 size-4 shrink-0 text-red"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-base font-bold text-charcoal">
                Consider an online agent if:
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate">
                {[
                  "You are confident conducting your own viewings",
                  "Your property is straightforward and easy to sell",
                  "Minimising upfront costs is your priority",
                  "You are experienced with the selling process",
                  "You are comfortable negotiating directly with buyers",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="mt-0.5 size-4 shrink-0 text-slate"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "98%", label: "of UK sales handled by high street agents" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "\u00a3999+", label: "typical online agent flat fee" },
            { stat: "1.18%", label: "average UK high street agent fee" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/how-to-choose-estate-agent",
                title: "How to choose an estate agent",
                desc: "Eight things to evaluate before instructing an agent.",
              },
              {
                href: "/guides/estate-agent-fees",
                title: "Estate agent fees",
                desc: "What agents charge by region and how to negotiate.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Great marketing levels the playing field
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Whether you are a high street agent competing with online
            disruptors or an independent looking to punch above your
            weight, QuiteRight gives you video marketing that wins
            instructions and sells properties.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
